import { useEffect, useState } from "react";
import {
	UserIcon,
	UserGroupIcon,
	BuildingOfficeIcon,
	ChevronRightIcon,
	ClockIcon,
	CheckBadgeIcon,
	MapPinIcon,
	ShieldCheckIcon as ShieldCheckIconOutline,
	HeartIcon as HeartIconOutline,
	SparklesIcon as SparklesIconOutline,
	UserCircleIcon as UserCircleIconOutline,
	HandRaisedIcon as HandRaisedIconOutline,
	Bars3Icon,
	XMarkIcon,
} from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";

import "../Signup/landing.scss";
import PublicHeader from "components/Header/PublicHeader";

const steps = [
	{
		number: 1,
		title: "Create Your Secure Account",
		description:
			"Create your account online with easy-to-read steps. You can pause at any time, save your progress, and review your entries with a loved one.",
	},
	{
		number: 2,
		title: "Share Your Health History",
		description: "We sit with you (or a loved one) and ask for your medications and doctor information so our therapists can provide safe, coordinated care. This secure form ensures all the necessary Medicare paperwork is ready.",
	},
	{
		number: 3,
		title: "Get Matched With Care",
		description:
			"Simply pick a time that works for you. We will pair you with a licensed therapist specialized in your specific needs and confirm the details before we arrive.",
	},
];

const HowItWorks = () => {
	const [isAuthenticated, setIsAuthenticated] = useState(false);

	useEffect(() => {
		const token = localStorage.getItem("token");
		setIsAuthenticated(!!token);
	}, []);

	useEffect(() => {
		const htmlElement = document.documentElement;
		htmlElement.classList.add("tailwind-html");
		document.body.classList.add("tailwind-body");

		return () => {
			htmlElement.classList.remove("tailwind-html");
			document.body.classList.remove("tailwind-body");
		};
	}, []);

	return (
		<div className="landing-page bg-white">
			<div className="min-h-screen bg-slate-50">
				{/* Header */}
				<PublicHeader />

				{/* Main Content */}
				<main className="relative isolate bg-white">
					{/* Hero */}
					<section className="mx-auto max-w-7xl px-6 lg:px-8 py-24 sm:py-32">
						<div className="grid gap-16 lg:grid-cols-[1.1fr_0.9fr] items-center">
							<div className="text-center lg:text-left">
								<div className="inline-flex items-center rounded-full px-4 py-1 text-sm font-medium bg-sky-50 text-sky-700 ring-1 ring-inset ring-sky-700/10 mb-6">
									<SparklesIconOutline className="h-4 w-4 mr-2 text-sky-600" />
									How It Works
								</div>
								<h1 className="text-4xl font-bold tracking-tight sm:text-5xl text-slate-900">
									Simple steps to bring care to your door
								</h1>
								<div className="mt-6 space-y-6 text-lg leading-8 text-gray-600">
									<p>
										We have designed our booking process specifically for older
										adults and their families. It involves just three gentle steps, each with clear, large-print
										instructions so you always know what comes next.
									</p>
									<p>
										You can move at your own pace to create your account, share your health history securely, and get matched with a therapist selected specifically for your needs.
									</p>
								</div>
								<div className="mt-10 flex flex-wrap justify-center lg:justify-start gap-4">
									<Link
										to="/auth/signup"
										className="inline-flex items-center justify-center rounded-full bg-sky-500 px-8 py-4 text-base font-semibold text-white shadow-lg transition hover:bg-sky-600"
									>
										Book Your First Visit
										<ChevronRightIcon className="ml-2 h-5 w-5" />
									</Link>
								</div>
							</div>
							<div className="relative">
								<div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-sky-100 to-white blur-3xl opacity-70 -z-10"></div>
								<div className="rounded-3xl border border-slate-100 bg-white p-8 shadow-2xl ring-1 ring-slate-200 space-y-6">
									<div className="flex items-start gap-4">
										<div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-sky-50">
											<CheckBadgeIcon className="h-6 w-6 text-sky-600" />
										</div>
										<div>
											<p className="text-base font-semibold text-slate-900 flex">Medicare-friendly</p>
											<p className="text-sm text-slate-500">Benefit verification before every first visit</p>
										</div>
									</div>
									<div className="grid gap-4 text-sm text-slate-600">
										<div className="flex items-center gap-3 rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3">
											<MapPinIcon className="h-5 w-5 text-sky-500" />
											Serving Greater Boston area
										</div>
										<div className="flex items-center gap-3 rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3">
											<BuildingOfficeIcon className="h-5 w-5 text-sky-500" />
											In-home visits (no technology needed for therapy)
										</div>
										<div className="flex items-center gap-3 rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3">
											<ClockIcon className="h-5 w-5 text-sky-500" />
											Flexible scheduling that works around home routines
										</div>
										<div className="flex items-center gap-3 rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3">
											<HandRaisedIconOutline className="h-5 w-5 text-sky-500" />
											Dedicated support team for questions along the way
										</div>
									</div>
								</div>
							</div>
						</div>
					</section>

					{/* Steps */}
					<section className="mx-auto max-w-7xl px-6 lg:px-8 py-24 sm:py-32 bg-gradient-to-b from-white to-sky-50">
						<div className="mx-auto max-w-3xl text-center">
							<p className="text-sm font-semibold uppercase tracking-wide text-sky-600">Only three steps</p>
							<h2 className="mt-4 text-3xl font-bold text-slate-900 sm:text-4xl">From registration to your in-home visit</h2>
							<p className="mt-4 text-lg text-gray-600">
								We designed each step for older adults: plain language, large text, and clear instructions that you can complete on your own or alongside a family member.
							</p>
						</div>
						<div className="relative mx-auto mt-16 max-w-3xl">
							<div className="hidden sm:block absolute left-6 top-0 bottom-0 w-px bg-gradient-to-b from-sky-200 via-sky-100 to-transparent"></div>
							<div className="space-y-10">
								{steps.map((step) => (
									<div key={step.number} className="relative flex flex-col sm:flex-row gap-6">
										<div className="relative z-10 flex items-center justify-center sm:justify-start">
											<span className="flex h-12 w-12 items-center justify-center rounded-full bg-white ring-2 ring-sky-200 text-lg font-bold text-sky-700 shadow-sm">
												{step.number}
											</span>
										</div>
										<div className="flex-1 rounded-2xl border border-slate-100 bg-white p-6 shadow-sm hover:shadow-md transition-all duration-300">
											<h3 className="text-xl font-semibold text-slate-900">{step.title}</h3>
											<p className="mt-3 text-base leading-7 text-slate-600">{step.description}</p>
										</div>
									</div>
								))}
							</div>
						</div>
					</section>

					{/* CTA */}
					<section className="mx-auto max-w-7xl px-6 lg:px-8 py-24 sm:py-12">
						<div className="relative overflow-hidden rounded-3xl border border-slate-100 bg-white px-6 py-16 text-center shadow-xl sm:px-16">
							<div className="absolute inset-0 pointer-events-none">
								<div className="absolute inset-y-0 left-0 w-1/2 bg-gradient-to-r from-sky-50/80 to-transparent"></div>
								<div className="absolute inset-y-0 right-0 w-1/2 bg-gradient-to-l from-rose-50/80 to-transparent"></div>
							</div>
							<div className="relative">
								<h2 className="mx-auto max-w-2xl text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
									Ready to start your visit?
								</h2>
								<p className="mx-auto mt-4 max-w-xl text-lg leading-8 text-slate-600">
									It only takes a few minutes to create your account and see if you are eligible. Our team is standing by if you need help along the way.
								</p>
								<p className="mx-auto mt-4 max-w-xl text-sm leading-6 text-slate-500">
									This is not a guarantee of coverage or payment. We’ll confirm benefits together before your first visit.
								</p>
								<div className="mt-8 flex flex-wrap items-center justify-center gap-4">
									<Link
										to="/auth/signup"
										className="inline-flex items-center justify-center rounded-full bg-sky-500 px-8 py-3 text-base font-semibold text-white shadow-lg transition hover:bg-sky-600"
									>
										Get Started
									</Link>
								</div>
							</div>
						</div>
					</section>
				</main>
			</div>
		</div>
	);
};

export default HowItWorks;
